import { createSelector, nanoid } from '@reduxjs/toolkit';
import { IWebFormContent } from '@qodly/shared';
import { EWebFormStyleClassScope, IWebFormStyleClass } from '@qodly/shared';
import { ITab } from '@qodly/shared';
import { AppState } from '../modules';
import { selectTabByPath } from './explorer';

export const THEME_CLASSES = [
  'rounded-full',
  'rounded-lg',
  'border-gray-400',
  'border-2',
  'bg-red-500',
  'bg-blue-500',
  'bg-indigo-500',
  'bg-transparent',
  'bg-primary-dark',
  'bg-green-500',
  'bg-yellow-400',
  'text-gray-500',
  'text-4xl',
  'text-white',
  'px-4',
  'py-2',
  'primary',
  'info',
  'secondary',
  'success',
  'warning',
  'danger',
  'w-10',
  'w-20',
  'w-40',
  'w-60',
  'w-80',
  'mb-2',
  'mb-3',
  'shadow',
  'shadow-lg',
  'shadow-xl',
  'shadow-2xl',
];

const themeClasses = THEME_CLASSES.map<IWebFormStyleClass>((name) => ({
  id: nanoid(),
  content: '',
  name,
  parentId: null,
  scope: EWebFormStyleClassScope.THEME,
}));

export const selectWebformStylesByPath = (path: string) =>
  createSelector(
    selectTabByPath(path),
    (tab: ITab<IWebFormContent> | undefined) =>
      tab?.content?.metadata?.styles || [],
  );

export const selectStylesByPath = (
  path: string,
  scopes: EWebFormStyleClassScope[],
) =>
  createSelector(
    selectWebformStylesByPath(path),
    (styles): IWebFormStyleClass[] => {
      return scopes
        .map<IWebFormStyleClass[]>((scope) => {
          switch (true) {
            case scope === EWebFormStyleClassScope.LOCAL: {
              return styles.map((style) => ({
                ...style,
                scope: EWebFormStyleClassScope.LOCAL,
              }));
            }
            case scope === EWebFormStyleClassScope.THEME: {
              return themeClasses;
            }
            default:
              return [];
          }
        })
        .flat();
    },
  );

export const selectCSSClassesByPath = (path: string) =>
  createSelector(
    selectWebformStylesByPath(path),
    (styles): IWebFormStyleClass[] => {
      return [
        ...styles.map((style) => ({
          ...style,
          scope: EWebFormStyleClassScope.LOCAL,
        })),
        ...themeClasses,
      ];
    },
  );

export const selectSharedCSS = createSelector(
  (state: AppState) => state,
  (state) => [
    ...(state.sharedCSS?.styles || []).map((style) => ({
      ...style,
      scope: EWebFormStyleClassScope.SHARED,
    })),
  ],
);
